"use client";

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from "react";
import { Language, I18nContextType } from "./types";
import { translate } from "./index";

const I18nContext = createContext<I18nContextType | undefined>(undefined);

interface I18nProviderProps {
  children: ReactNode;
  defaultLanguage?: Language;
}

export function I18nProvider({
  children,
  defaultLanguage = "en",
}: I18nProviderProps) {
  const [language, setLanguage] = useState<Language>(defaultLanguage);
  
  // ブラウザの言語設定を検出
  useEffect(() => {
    // クライアントサイドでのみ実行
    if (typeof window === 'undefined') return;

    try {
      const browserLang = navigator.language;
      let detectedLang: Language = defaultLanguage;

      // 言語コードのマッピング
      if (browserLang.startsWith("zh-TW") || browserLang.startsWith("zh-HK")) {
        detectedLang = "zhTW";
      } else if (browserLang.startsWith("zh")) {
        detectedLang = "zh";
      } else {
        // 単純なプレフィックスマッチ (ja-JP -> ja)
        const code = browserLang.split("-")[0];
        // supportedLanguagesはインポートが必要だが、循環参照を避けるためここで直接チェックするか、
        // または `translations` オブジェクトのキーをチェックする
        // ここでは安全のため、明示的なリストと照合する、もしくはindexからインポートする
        // コード簡略化のため、既存のtranslationsオブジェクトがインポートされている前提でチェック
        // ただし、provider.tsxではtranslate関数しかインポートしていないため、
        // supportedLanguagesを利用可能にする必要がある。
        // 今回は実装をシンプルにするため、主要言語をハードコードチェックでも良いが、
        // ベストプラクティスとしてindex.tsxの変更を待ってからインポートするのが正しい。
        // ここでは一旦、index.tsxの変更が適用されることを前提に、後続の修正でインポートを追加する形にする。
        // しかし、1回のステップで完結させるため、ここで動的にrequireするか、indexの修正完了を待つ。
        // 実際にはParallel callなので、次のステップでimportsを修正する。
        
        // 簡易的な判定ロジック (後でimportsを修正)
        const supported = ["ja", "en", "zh", "zhTW", "ko", "es", "de", "fr"];
        if (supported.includes(code)) {
            detectedLang = code as Language;
        }
      }

      setLanguage(detectedLang);
      document.documentElement.lang = detectedLang;
    } catch (e) {
      console.error("Failed to detect language", e);
    }
  }, [defaultLanguage]);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>) => {
      return translate(key, language, params);
    },
    [language]
  );

  const value: I18nContextType = {
    language,
    setLanguage,
    t,
  };

  // 言語が変更されたときにhtmlタグも更新
  useEffect(() => {
    if (typeof window !== 'undefined') {
        document.documentElement.lang = language;
    }
  }, [language]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18nContext() {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error("useI18nContext must be used within an I18nProvider");
  }
  return context;
}
